import { useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";

const SEVERITY_COLOR = {
  high: "border-l-sovereign bg-sovereign/5",
  medium: "border-l-warning-amber bg-warning-amber/5",
  low: "border-l-truth-green bg-truth-green/5",
};

export const MetadataAuditPanel = ({ auditId, initialResult, readOnly = false }) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(initialResult || null);

  const runScan = async () => {
    if (!url) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/audits/${auditId}/metadata-scan`, { url });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not scan that URL.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-surface-border bg-surface-card p-6" data-testid="metadata-audit-panel">
      <h3 className="mb-1 font-heading text-lg font-bold text-white">Metadata Audit Tool</h3>
      <p className="mb-4 text-sm text-slate-400">Scan your live website and see exactly which line of code confuses the AI.</p>
      {readOnly && !result && (
        <p className="text-sm text-slate-500" data-testid="metadata-audit-readonly-empty">Ask the account owner to run this scan.</p>
      )}
      {!readOnly && (
        <div className="mb-6 flex gap-2">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="yourbrand.com"
            className="flex-1 rounded-md border border-surface-border bg-obsidian px-3 py-2 text-sm text-white outline-none focus:border-openai-cyan"
            data-testid="metadata-audit-url-input"
          />
          <Button onClick={runScan} disabled={loading} className="gap-2 bg-openai-cyan text-obsidian hover:bg-openai-cyan/90" data-testid="metadata-audit-scan-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Scan Site
          </Button>
        </div>
      )}

      {result?.error && <p className="text-sm text-sovereign" data-testid="metadata-audit-error">{result.error}</p>}

      {result?.issues && (
        <div className="space-y-3" data-testid="metadata-audit-results">
          {result.issues.length === 0 && (
            <p className="text-sm text-truth-green" data-testid="metadata-audit-no-issues">
              No AI-confusing issues found - this page looks crawlable and well-structured.
            </p>
          )}
          {result.issues.map((issue, i) => (
            <div key={i} className={`rounded-md border-l-4 p-4 ${SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.low}`} data-testid={`metadata-issue-${i}`}>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">{issue.issue}</span>
                {issue.line_number && <span className="font-mono text-xs text-slate-500">Line {issue.line_number}</span>}
              </div>
              {issue.code_snippet && (
                <pre className="mb-2 overflow-x-auto whitespace-pre-wrap break-all rounded bg-obsidian p-2 font-mono text-xs text-slate-400">{issue.code_snippet}</pre>
              )}
              <p className="mb-2 text-xs text-slate-400">{issue.why_it_confuses_ai}</p>
              {issue.suggested_fix && (
                <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded border border-truth-green/30 bg-truth-green/5 p-2 font-mono text-xs text-truth-green">
                  {issue.suggested_fix}
                </pre>
              )}
            </div>
          ))}
          {result.passed_checks?.length > 0 && (
            <details className="rounded-md border border-surface-border bg-obsidian p-3" data-testid="metadata-audit-passed-checks">
              <summary className="cursor-pointer text-xs font-semibold text-slate-400">{result.passed_checks.length} checks passed</summary>
              <ul className="mt-2 space-y-1">
                {result.passed_checks.map((check, i) => (
                  <li key={i} className="text-xs text-slate-500">- {check}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
